import React from 'react'
import { keyframes } from '@emotion/react'
import { navigate } from 'gatsby'
import { BellIcon } from '@chakra-ui/icons'
import {
    Box,
    Text,
    Button,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverArrow,
    PopoverCloseButton,
    PopoverHeader,
    PopoverBody,
    Avatar,
    Badge,
    Flex,
    Spacer,
} from '@chakra-ui/react'

import SharedStack from '~components/shared/wrappers/shared-stack'
import { useUserNotifications } from '~contexts/notifications-provider'
import { useClinic } from '~contexts/clinic-context'
import {
    UserNotificationFieldsFragment,
    UserNotificationType,
} from '~graphql/generated/graphql'
import MentionText from '~components/shared/text/mention-text'
import { getUserFullName } from '~utils/helpers'

const ripple = keyframes`
    0% {
        transform: scale(1);
        opacity: 0.6;
    }
    100% {
        transform: scale(2.4);
        opacity: 0;
    }
`

/**
 * Header notifications bell. Product announcements live in What's New.
 */
export default function TempAlertButton() {
    const {
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
    } = useUserNotifications()
    const { clinicGroup, selectClinicGroupById } = useClinic()

    if (notifications.length === 0) return null

    const shouldRing = unreadCount > 0

    return (
        <Popover placement="bottom-end">
            {({ onClose }) => (
                <>
                    <PopoverTrigger>
                        <Box
                            as="button"
                            aria-label="Notifications"
                            cursor="pointer"
                            p={1}
                            rounded="full"
                            position="relative"
                            display="inline-flex"
                            alignItems="center"
                            justifyContent="center"
                            _hover={{ bg: 'brand.50' }}
                        >
                            {shouldRing && (
                                <Box
                                    position="absolute"
                                    w="100%"
                                    h="100%"
                                    borderRadius="full"
                                    border="2px solid"
                                    borderColor="brand.400"
                                    animation={`${ripple} 2s ease-out infinite`}
                                />
                            )}
                            <BellIcon
                                boxSize={5}
                                color="brand.500"
                                position="relative"
                            />
                            {unreadCount > 0 && (
                                <Badge
                                    position="absolute"
                                    top="-2px"
                                    right="-2px"
                                    colorScheme="red"
                                    borderRadius="full"
                                    fontSize="2xs"
                                    minW="18px"
                                    h="18px"
                                    px={1}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    boxShadow="0 0 0 2px white"
                                >
                                    {unreadCount > 99 ? '99+' : unreadCount}
                                </Badge>
                            )}
                        </Box>
                    </PopoverTrigger>
                    <PopoverContent w="360px">
                        <PopoverArrow />
                        <PopoverCloseButton />
                        <NotificationsSection
                            notifications={notifications}
                            unreadCount={unreadCount}
                            onItemClick={async notification => {
                                if (notification.read_at == null) {
                                    await markAsRead(notification.id)
                                }
                                // Group-scoped pages (e.g. Enterprise
                                // Onboarding) only load with group
                                // permissions, so leave clinic mode first.
                                const targetClinicGroupId =
                                    getNotificationClinicGroupId(notification)
                                if (
                                    targetClinicGroupId &&
                                    targetClinicGroupId !== clinicGroup?.id
                                ) {
                                    selectClinicGroupById(targetClinicGroupId)
                                }
                                if (notification.link_url) {
                                    navigate(notification.link_url)
                                }
                                onClose()
                            }}
                            onMarkAllRead={() => {
                                void markAllAsRead()
                            }}
                        />
                    </PopoverContent>
                </>
            )}
        </Popover>
    )
}

interface NotificationsSectionProps {
    notifications: UserNotificationFieldsFragment[]
    unreadCount: number
    onItemClick: (
        notification: UserNotificationFieldsFragment,
    ) => Promise<void> | void
    onMarkAllRead: () => void
}

function NotificationsSection({
    notifications,
    unreadCount,
    onItemClick,
    onMarkAllRead,
}: NotificationsSectionProps) {
    return (
        <>
            <PopoverHeader
                px={3}
                pr={10}
                py={2}
                border="none"
            >
                <Flex align="center">
                    <Text fontSize="sm" fontWeight="semibold">
                        Notifications
                        {unreadCount > 0 && (
                            <Text
                                as="span"
                                ml={1.5}
                                color="gray.500"
                                fontWeight="normal"
                            >
                                ({unreadCount})
                            </Text>
                        )}
                    </Text>
                    <Spacer />
                    {unreadCount > 0 && (
                        <Button
                            size="xs"
                            variant="ghost"
                            colorScheme="brand"
                            onClick={onMarkAllRead}
                            fontWeight="semibold"
                        >
                            Mark all read
                        </Button>
                    )}
                </Flex>
            </PopoverHeader>
            <PopoverBody
                p={0}
                maxH="360px"
                overflowY="auto"
                pb={1}
            >
                {notifications.map((notification, index) => (
                    <NotificationItem
                        key={notification.id}
                        notification={notification}
                        showDivider={index < notifications.length - 1}
                        onClick={() => {
                            void onItemClick(notification)
                        }}
                    />
                ))}
            </PopoverBody>
        </>
    )
}

function NotificationItem({
    notification,
    showDivider,
    onClick,
}: {
    notification: UserNotificationFieldsFragment
    showDivider: boolean
    onClick: () => void
}) {
    const isUnread = notification.read_at == null
    const actor = notification.created_by_user
    const actorName = actor ? getUserFullName(actor) : 'A teammate'

    return (
        <Box
            as="button"
            w="full"
            textAlign="left"
            position="relative"
            px={3}
            py={2.5}
            bg={isUnread ? 'brand.50' : 'transparent'}
            _hover={{ bg: isUnread ? 'brand.100' : 'gray.50' }}
            onClick={onClick}
            cursor="pointer"
            borderBottom={showDivider ? '1px solid' : 'none'}
            borderColor="gray.100"
        >
            <SharedStack row align="flex-start" spacing={3}>
                <Avatar
                    size="sm"
                    name={actorName}
                    src={actor?.avatar_url || undefined}
                    flexShrink={0}
                />
                <Box flex={1} minW={0} pr={isUnread ? 4 : 0}>
                    <Flex align="baseline" gap={1.5}>
                        <Text
                            fontSize="sm"
                            fontWeight="semibold"
                            color="gray.800"
                            noOfLines={1}
                        >
                            {actorName}
                        </Text>
                        <Text
                            fontSize="xs"
                            color="gray.500"
                            flexShrink={0}
                            ml="auto"
                        >
                            {formatRelativeShort(notification.created_at)}
                        </Text>
                    </Flex>
                    <Text fontSize="xs" color="gray.600" noOfLines={1} mt={0.5}>
                        {getNotificationLeadIn(notification)}
                    </Text>
                    {notification.body && (
                        <Text
                            fontSize="xs"
                            color="gray.700"
                            mt={1}
                            noOfLines={2}
                            sx={{ wordBreak: 'break-word' }}
                        >
                            <MentionText text={notification.body} />
                        </Text>
                    )}
                </Box>
            </SharedStack>
            {isUnread && (
                <Box
                    position="absolute"
                    top="50%"
                    right={3}
                    transform="translateY(-50%)"
                    w={2}
                    h={2}
                    borderRadius="full"
                    bg="brand.500"
                />
            )}
        </Box>
    )
}

function getNotificationLeadIn(
    notification: UserNotificationFieldsFragment,
): string {
    // Onboarding board mentions reuse MentionedInInternalNote (no dedicated
    // notification type); the payload marker is what distinguishes them. MUST
    // stay in sync with the server's ONBOARDING_NOTIFICATION_SURFACE.
    if (notification.payload?.surface === 'onboarding') {
        return 'mentioned you in Enterprise Onboarding'
    }
    switch (notification.type) {
        case UserNotificationType.MentionedInTeamChat:
            return 'mentioned you in Team Chat'
        case UserNotificationType.MentionedInInternalNote:
            return 'mentioned you in an Internal Note'
        default:
            return 'sent you a notification'
    }
}

/**
 * Clinic group the notification's deep link is scoped to, read from the
 * server-set `payload.clinic_group_id` (currently Enterprise Onboarding
 * mentions/assignments). Undefined for clinic-scoped notifications.
 */
function getNotificationClinicGroupId(
    notification: UserNotificationFieldsFragment,
): string | undefined {
    const clinicGroupId: unknown = notification.payload?.clinic_group_id
    return typeof clinicGroupId === 'string' && clinicGroupId
        ? clinicGroupId
        : undefined
}

/**
 * Produces a one- or two-character relative timestamp (e.g. "now", "4m",
 * "3h", "2d", "5w"). Falls back to "MMM d" once we're past 4 weeks so
 * older notifications still get a meaningful label.
 */
function formatRelativeShort(timestamp: number): string {
    const diffMs = Date.now() - timestamp
    if (diffMs < 60_000) return 'now'

    const minutes = Math.floor(diffMs / 60_000)
    if (minutes < 60) return `${minutes}m`

    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h`

    const days = Math.floor(hours / 24)
    if (days < 7) return `${days}d`

    const weeks = Math.floor(days / 7)
    if (weeks < 4) return `${weeks}w`

    return new Date(timestamp).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
    })
}
